import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "./DateField";

function formatErr(detail) {
  if (!detail) return "Aktion fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

/**
 * Settings section for "Besondere Tage" — personal calendar markers that
 * appear in the planner as a visual hint (soft yellow day label + dimmed
 * project bars). Milestones remain placeable on these days.
 */
export default function SpecialDaysSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  // Draft for the inline "new entry" row.
  const [draft, setDraft] = useState({ title: "", start_date: "", end_date: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/special-days`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleAdd = async () => {
    const title = (draft.title || "").trim();
    if (!title) {
      toast.error("Bitte einen Titel eingeben.");
      return;
    }
    if (!draft.start_date) {
      toast.error("Bitte ein Startdatum auswählen.");
      return;
    }
    if (draft.end_date && draft.end_date < draft.start_date) {
      toast.error("Enddatum darf nicht vor Startdatum liegen.");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/special-days`, {
        title,
        start_date: draft.start_date,
        end_date: draft.end_date || null,
      });
      setDraft({ title: "", start_date: "", end_date: "" });
      toast.success("Besonderer Tag hinzugefügt");
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/special-days/${id}`);
      toast.success("Eintrag gelöscht");
      setItems((cur) => cur.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-8 mb-4" data-testid="settings-special-days-card">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
        Persönliche Hinweise
      </div>
      <div className="text-sm text-slate-700 mb-1">Besondere Tage</div>
      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        Markiere wichtige private Tage (z. B. Urlaub, Geburtstag, Arzttermin).
        Im Planer erscheinen diese Tage mit einem sanften gelben Hintergrund
        und gedimmten Projektbalken — als visueller Hinweis. Meilensteine bleiben
        platzierbar, es gibt keine Sperrlogik. Einträge, deren Enddatum
        vollständig in der Vergangenheit liegt, werden automatisch entfernt.
      </p>

      {/* Existing entries */}
      <div className="space-y-2 mb-4">
        {loading && (
          <div className="text-xs text-slate-400" data-testid="special-days-loading">Wird geladen…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-xs text-slate-400 italic" data-testid="special-days-empty">
            Noch keine besonderen Tage hinterlegt.
          </div>
        )}
        {items.map((it) => (
          <SpecialDayRow key={it.id} item={it} onDelete={() => handleDelete(it.id)} />
        ))}
      </div>

      {/* Add new entry */}
      <div className="border-t border-slate-100 pt-4">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-2">
          Neuer Eintrag
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px_auto] gap-2 items-start">
          <Input
            type="text"
            placeholder="z. B. Urlaub, Geburtstag…"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="h-9"
            data-testid="special-day-title-input"
          />
          <DateField
            value={draft.start_date}
            onChange={(iso) => setDraft((d) => ({ ...d, start_date: iso }))}
            placeholder="Start TT.MM.JJJJ"
            testId="special-day-start-input"
          />
          <DateField
            value={draft.end_date}
            onChange={(iso) => setDraft((d) => ({ ...d, end_date: iso }))}
            placeholder="Ende (optional)"
            testId="special-day-end-input"
          />
          <Button
            type="button"
            onClick={handleAdd}
            disabled={adding}
            className="h-9 bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
            data-testid="special-day-add-button"
          >
            <Plus className="w-3.5 h-3.5" />
            {adding ? "…" : "Hinzufügen"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function fmtDe(iso) {
  if (!iso) return "";
  const parts = String(iso).slice(0, 10).split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function SpecialDayRow({ item, onDelete }) {
  const rangeLabel = item.end_date && item.end_date !== item.start_date
    ? `${fmtDe(item.start_date)} – ${fmtDe(item.end_date)}`
    : fmtDe(item.start_date);
  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm bg-amber-50/60 border border-amber-100"
      data-testid={`special-day-row-${item.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-900 truncate">{item.title}</div>
        <div className="text-[11px] tabular-nums text-slate-500">{rangeLabel}</div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="text-slate-400 hover:text-rose-600 p-1"
        aria-label="Eintrag löschen"
        data-testid={`special-day-delete-${item.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
